import { useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { TbIcon, TbIllustration } from 'components/Figma';
import { useUploadCostConfigFile } from 'components/profitability/configQueryHooks';
import { toast } from 'sonner';
import { Button } from 'components/Button';

export interface UploadCostConfigModalProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
}

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

const ACCEPTED_FILE_EXTENSION = '.xlsx';
const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1GB

const INSTRUCTION_BULLET_IDS = [
  'profitability.cost-configurations.upload.instructions.bullet1',
  'profitability.cost-configurations.upload.instructions.bullet2',
  'profitability.cost-configurations.upload.instructions.bullet3',
  'profitability.cost-configurations.upload.instructions.bullet4',
];

export function UploadCostConfigModal({ open, onClose, onUploadSuccess = undefined }: UploadCostConfigModalProps) {
  const intl = useIntl();
  const uploadCostConfigFile = useUploadCostConfigFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [status, setStatus] = useState<UploadStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [isDragActive, setIsDragActive] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const resetState = () => {
    setStatus('idle');
    setProgress(0);
    setIsDragActive(false);
    setFileName(null);
    setErrorMessage(undefined);
    abortControllerRef.current = null;
  };

  const handleClose = () => {
    abortControllerRef.current?.abort();
    resetState();
    onClose();
  };

  const validateFile = (file: File): string | undefined => {
    if (!file.name.toLowerCase().endsWith(ACCEPTED_FILE_EXTENSION)) {
      return intl.formatMessage({ id: 'profitability.cost-configurations.upload.failed.invalidType' });
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return intl.formatMessage({ id: 'profitability.cost-configurations.upload.failed.tooLarge' });
    }
    return undefined;
  };

  const startUpload = async (file: File) => {
    setFileName(file.name);

    const validationError = validateFile(file);
    if (validationError) {
      setStatus('error');
      setErrorMessage(validationError);
      return;
    }

    setStatus('uploading');
    setProgress(0);
    setErrorMessage(undefined);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const promise = uploadCostConfigFile(file, setProgress, abortController.signal);

    const result = await promise;

    if (result.success) {
      toast.success(
        intl.formatMessage({ id: 'profitability.cost-configurations.upload.toaster.success' }, { fileName: file.name }),
        { duration: 5000, position: 'top-right' }
      );
      setStatus('success');
      onUploadSuccess?.();
    } else {
      setStatus('error');
      setErrorMessage(result.errors.join(' '));
    }
  };

  const handleFileSelected = (files: FileList | null) => {
    const file = files?.[0];
    if (file) {
      void startUpload(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    handleFileSelected(event.dataTransfer.files);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      data-testid="upload-cost-config-modal"
      PaperProps={{
        className: 'w-[772px] max-w-[90vw] h-[698px] max-h-[90vh] rounded-lg bg-snow-50 shadow-elevation-medium',
      }}
    >
      <DialogTitle className="shrink-0 border-b border-border-default p-4">
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#374151' }}>
          <FormattedMessage id="profitability.cost-configurations.upload.title" />
        </Typography>
        <Button
          icon="Cross"
          aria-label="close"
          onClick={handleClose}
          className="ml-auto"
          data-testid="upload-modal-close"
        />
      </DialogTitle>

      <DialogContent className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
        <div className="flex flex-col gap-4 rounded-md bg-snow-100 p-4">
          <div className="flex items-center gap-4">
            <TbIcon name="List" size="md" className="text-ash-900" />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#14b8a6' }}>
              <FormattedMessage id="profitability.cost-configurations.upload.instructions.title" />
            </Typography>
          </div>
          <div className="flex flex-col gap-2">
            {INSTRUCTION_BULLET_IDS.map(id => (
              <div key={id} className="flex flex-row items-start gap-2">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-ash-900" />
                <Typography variant="body2" sx={{ fontSize: '13px', lineHeight: 1.5, color: '#374151' }}>
                  <FormattedMessage id={id} />
                </Typography>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-6 rounded-lg bg-snow-100 p-4">
          {status === 'idle' && (
            <div
              className={`flex flex-1 flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed py-12 text-center transition-colors ${
                isDragActive ? 'border-turquoise-500 bg-snow-50' : 'border-snow-500 bg-snow-50'
              }`}
              data-testid="upload-dropzone"
              onDragOver={event => {
                event.preventDefault();
                setIsDragActive(true);
              }}
              onDragLeave={() => setIsDragActive(false)}
              onDrop={handleDrop}
            >
              <TbIcon name="UploadCloud" size="xxl" className="h-12 w-12" />
              <div className="flex flex-col items-center gap-1">
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#374151' }}>
                  <FormattedMessage id="profitability.cost-configurations.upload.dropzone.text" />
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '13px', color: '#6b7280' }}>
                  <FormattedMessage id="profitability.cost-configurations.upload.dropzone.subtext" />
                </Typography>
              </div>
              <Button
                priority="ghost"
                className="text-turquoise-500"
                onClick={handleBrowseClick}
                data-testid="upload-browse-button"
              >
                <FormattedMessage id="profitability.cost-configurations.upload.dropzone.browseLink" />
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_EXTENSION}
                className="hidden"
                data-testid="upload-file-input"
                onChange={event => handleFileSelected(event.target.files)}
              />
            </div>
          )}

          {status === 'uploading' && (
            <div
              className="flex flex-1 flex-col justify-center gap-3 rounded-lg bg-snow-50 p-6"
              data-testid="upload-progress"
            >
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#374151' }}>
                <FormattedMessage id="profitability.cost-configurations.upload.progress.title" />
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '13px', color: '#6b7280' }}>
                <FormattedMessage
                  id="profitability.cost-configurations.upload.progress.description"
                  values={{ fileName }}
                />
              </Typography>
              <LinearProgress variant="determinate" value={progress} data-testid="upload-progress-bar" />
              <Typography variant="body2" sx={{ alignSelf: 'flex-end', fontSize: '13px', color: '#6b7280' }}>
                {progress}%
              </Typography>
            </div>
          )}

          {status === 'success' && (
            <div
              className="flex flex-1 flex-col items-center justify-center gap-2 rounded-lg bg-snow-50 p-6 text-center"
              data-testid="upload-success"
            >
              <TbIllustration name="ProcessCompleted" size="sm" />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#65a30d' }}>
                <FormattedMessage id="profitability.cost-configurations.upload.success.title" />
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '13px', color: '#6b7280' }}>
                <FormattedMessage
                  id="profitability.cost-configurations.upload.success.description"
                  values={{ fileName }}
                />
              </Typography>
            </div>
          )}

          {status === 'error' && (
            <div
              className="flex flex-1 flex-col items-center justify-center gap-2 rounded-lg bg-snow-50 p-6 text-center"
              data-testid="upload-failure"
            >
              <TbIllustration name="ErrorGeneric" size="sm" />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f97316' }}>
                <FormattedMessage id="profitability.cost-configurations.upload.failed.title" />
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '13px', color: '#6b7280' }}>
                <FormattedMessage
                  id="profitability.cost-configurations.upload.failed.description"
                  values={{ fileName, error: errorMessage }}
                />
              </Typography>
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions className="shrink-0 justify-end gap-2 border-t border-border-default p-6">
        {(status === 'idle' || status === 'uploading') && (
          <Button priority="secondary" onClick={handleClose} data-testid="upload-modal-cancel">
            <FormattedMessage id="profitability.cost-configurations.upload.cancelButton" />
          </Button>
        )}
        {(status === 'success' || status === 'error') && (
          <Button onClick={handleClose} data-testid="upload-modal-done">
            <FormattedMessage id="general.button.close" />
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
